import { useCallback, useContext, useMemo, useState } from 'react'

import { connect } from 'react-redux'
import moment from 'moment'
import { isArray } from 'lodash'

import Settings from './Settings'
import { isQuotaInSchools } from '../../Quotas/SchoolQuotasView/utils'
import { selectUser } from '../../../../reducers/UserReducer'
import { TimePeriod, PeriodTypes } from '../../../../utils/timePeriods'
import { InternshipsSettings } from '../../../../Components/antd/Tables/DragTable/OverlaySettings'
import { useLocalStorage } from '../../../../hooks/UseLocalStorage'
import LOCAL_STORAGE_KEYS from '../../../../utils/localStorage'
import { firstOrDefault, getEntityOrNull, getIdOrNull, isEmptyArray, isNonEmptyArray } from '../../../../utils'
import useRequest from '../../../../hooks/useRequest'
import { getSchoolPeriods } from '../../../../utils/api/schoolPeriods'
import { Cases, InternshipStates, OrderBy } from '../../../../utils/constants'
import { SchoolPeriod } from '../../../../utils/entities/schoolPeriod'
import { getAllInternships, InternshipFilters } from '../../../../utils/api/internship'
import { Order, QueryParams } from '../../../../utils/entities/queries'
import { getAllQuotas, SerializationContexts as QuotaContexts } from '../../../../utils/api/quota'
import { getDifference, getQuotasMap, QuotasMap } from '../../../../Components/shared/Quotas/utils'
import PlacesOccupancyHeader from './PlacesOccupancyHeader'
import PlacesOccupancyTable from './PlacesOccupancyTable'
import { reduceBySector } from '../../../../Components/shared/Internship/utils'
import InternshipFormModal from '../../../../Providers/Forms/InternshipForm/InternshipFormModal'
import { Internship, InternshipProperties } from '../../../../utils/entities/internship'
import { Actions } from '../../../../utils/events'
import HighlightedColumnsProvider from '../../../../Providers/Quotas/HighlightedColumnsProvider'
import { getAllStudentPathways, SerializationContexts as PathwaysContexts } from '../../../../utils/api/studentPathway'
import InternshipToolBar from './InternshipToolBar'
import { API_DATE, getTimezoneLessMoment } from '../../../../utils/momentjs'
import { StudentPathway } from '../../../../utils/entities/studentPathway'
import { Quota } from '../../../../utils/entities/quota'
import { DEFAULT_SELECTED_VALUES } from '../../../../Components/antd/Tables/DragTable/DragTable'
import { getBySectors } from '../../../../utils/api/sectionGroup'
import { InstitutionContext } from '../../../../Providers/InstitutionProvider'

import '../../../../assets/quota-allocation.scss'

const KEYS_TO_VALIDATE = [
  InternshipProperties.SCHOOL,
  InternshipProperties.START_DATE,
  InternshipProperties.END_DATE,
  InternshipProperties.SECTOR
]

const { LABELS } = QuotaContexts
const { SECTOR, SECTION } = PathwaysContexts
const STEP = 10

const mapStateToProps = state => ({ user: selectUser(state) })

const PlacesOccupancyView = ({ user }) => {
  const { sectorsOrderByName } = useContext(InstitutionContext)

  const [period, setPeriod] = useState(new TimePeriod(moment(), 26, PeriodTypes.WEEK))
  const [isYearly, setIsYearly] = useState(false)
  const [schools, setSchools] = useState([])
  const [sectors, setSectors] = useState([])
  const [activeSectors, setActiveSectors] = useState([])
  const [selectedInternships, setSelectedInternships] = useState(DEFAULT_SELECTED_VALUES)
  const [sectionGroupsMap, setSectionGroupsMap] = useState({})
  const [internshipsMap, setInternshipsMap] = useState({})
  const [studentPathways, setStudentPathways] = useState([])
  const [rawQuotas, setRawQuotas] = useState({})
  const [page, setPage] = useState(1)

  const internshipParams = useMemo(() => new QueryParams({
    filters: new InternshipFilters({
      schools,
      sectors: [-1],
      states: Object.values(InternshipStates).filter(state => state !== InternshipStates.REFUSED)
    }),
    paginate: false
  }), [schools])

  const [overlaySettings, setOverlaySettings] = useLocalStorage(
    LOCAL_STORAGE_KEYS.QUOTAS_TABLE.OVERLAY_SETTINGS,
    new InternshipsSettings(),
    value => new InternshipsSettings(value)
  )

  const config = useMemo(() => ({ overlay: overlaySettings }), [overlaySettings])

  const holidays = useRequest({
    request: getSchoolPeriods,
    enabled: schools.length === 1,
    parameters: useMemo(() => ({
      filters: { school: getIdOrNull(schools[0]) },
      order: new Order({ by: OrderBy.START_DATE })
    }), [schools[0]]),
    select: useCallback(holidays => holidays.map(holiday => getIdOrNull(holiday) ? new SchoolPeriod(holiday) : null), [])
  })

  const rawQuotasMap = useMemo(() => Object.keys(rawQuotas).reduce((acc, key) => {
    acc[key] = getQuotasMap(rawQuotas[key])

    return acc
  }, {}), [rawQuotas])

  const quotasMap = useMemo(() => {
    const copyQuotasMap = { ...rawQuotasMap }

    activeSectors.map(sector => {
      if (sector.id in copyQuotasMap) {
        if (overlaySettings.filterQuotasBySchools) {
          copyQuotasMap[sector.id].places = copyQuotasMap[sector.id].places.filter(isQuotaInSchools(schools))
        }
      } else {
        copyQuotasMap[sector.id] = new QuotasMap()
      }
    })

    return copyQuotasMap
  }, [activeSectors, rawQuotasMap, schools, overlaySettings])

  const loadSectors = useCallback((sectors, period) => {
    if (isNonEmptyArray(sectors)) {
      const sectorIds = sectors.map(getIdOrNull)

      period = period.getDates(API_DATE)
      const { startDate, endDate } = period

      getAllQuotas(user, { contexts: [LABELS], startDate, endDate, sectors: sectorIds }).then(({ data }) => {
        const quotas = data.map(q => new Quota(q))

        setRawQuotas(rawQuotas => ({ ...rawQuotas, ...reduceBySector(quotas, sectors) }))
      })

      getAllStudentPathways(user, { contexts: [SECTION, SECTOR], sectors: sectorIds }).then(({ data }) => {
        const pathways = data.map(p => new StudentPathway(p))

        setStudentPathways(studentPathways => ({ ...studentPathways, ...reduceBySector(pathways, sectorIds) }))
      })

      getBySectors(user, { sectors: sectorIds, contexts: [LABELS] }).then(({ data }) => {
        setSectionGroupsMap(prev => ({ ...prev, ...data }))
      })

      const params = internshipParams.clone({
        filters: internshipParams.filters.clone({
          sectors: sectorIds,
          period
        })
      })

      getAllInternships(user, params.toCase(Cases.KEBAB)).then(({ data }) => {
        setInternshipsMap(map => ({ ...map, ...reduceBySector(data, sectors) }))
      })
    }
  }, [user, internshipParams])

  const handleSectorsChange = useCallback((modifiedSectors, shouldAdd) => {
    let newSectors = []

    if (isArray(modifiedSectors)) {
      const newActiveSectors = modifiedSectors.slice(0, STEP)
      newSectors = modifiedSectors

      setActiveSectors(newActiveSectors)

      setPage(1)

      loadSectors(newActiveSectors, period)
    } else if (shouldAdd) {
      newSectors = sectors.concat(modifiedSectors)

      if (page * STEP >= newSectors.length) {
        setActiveSectors(newSectors)

        loadSectors([modifiedSectors], period)
      }
    } else {
      newSectors = sectors.filter(s => s.id !== modifiedSectors.id)

      const newActiveSectors = newSectors.slice(0, page * STEP)

      setActiveSectors(newActiveSectors)

      const diff = getDifference(newActiveSectors, activeSectors)

      if (diff.length > 0) {
        loadSectors(diff, period)
      }
    }

    setSectors(newSectors)
  }, [sectors, activeSectors, page, period, loadSectors])

  const handleDateChange = useCallback(date => {
    const newPeriod = new TimePeriod(date, period.quantity, PeriodTypes.WEEK)
    const newActiveSectors = sectors.slice(0, STEP)

    setSelectedInternships(DEFAULT_SELECTED_VALUES)
    setPage(1)
    setActiveSectors(newActiveSectors)
    setPeriod(newPeriod)
    loadSectors(newActiveSectors, newPeriod)
  }, [sectors, period, loadSectors])

  const handlePeriodChange = useCallback(quantity => {
    const newPeriod = new TimePeriod(period.startDate, quantity, PeriodTypes.WEEK)
    const newActiveSectors = sectors.slice(0, STEP)

    setSelectedInternships(DEFAULT_SELECTED_VALUES)
    setPage(1)
    setActiveSectors(newActiveSectors)
    setPeriod(newPeriod)
    loadSectors(newActiveSectors, newPeriod)
  }, [sectors, period, loadSectors])

  const handleSelectSetting = useCallback((setting, isSelected) => {
    setOverlaySettings(current => current.clone({ [setting]: isSelected }))
  }, [])

  const handleScroll = useCallback(({ target }) => {
    if (target.scrollTop + target.offsetHeight >= (target.scrollHeight - 20) && (page * STEP) < sectors.length) {
      setPage(page => page + 1)

      const sectorsToBeLoaded = sectors.slice(page * STEP, (page + 1) * STEP)

      setActiveSectors(sectorsToBeLoaded)
      loadSectors(sectorsToBeLoaded, period)
    }
  }, [sectors, page, period, loadSectors])

  const handleInternshipsChange = useCallback((...params) => {
    const newInternshipsMap = { ...internshipsMap }
    let newSelectedInternships = [...selectedInternships.values]

    for (let iItemActionPairs = 0; iItemActionPairs < params.length; iItemActionPairs += 2) {
      const internship = params[iItemActionPairs]
      const action = params[iItemActionPairs + 1]
      const startDate = getTimezoneLessMoment(internship.startDate)
      const endDate = getTimezoneLessMoment(internship.endDate)

      if (!isNonEmptyArray(newInternshipsMap[internship.sector.id])) {
        newInternshipsMap[internship.sector.id] = []
      }

      if (action !== Actions.DELETE && (startDate > period.endDate || endDate < period.startDate)) {
        newInternshipsMap[internship.sector.id] = newInternshipsMap[internship.sector.id].filter(i => i.id !== internship.id)
        newSelectedInternships = newSelectedInternships.filter(i => i.id !== internship.id)
      } else if (action === Actions.CREATE && (startDate < period.endDate || endDate > period.startDate)) {
        newInternshipsMap[internship.sector.id] = newInternshipsMap[internship.sector.id].concat(internship)
      } else if (action === Actions.UPDATE) {
        const currentList = newInternshipsMap[internship.sector.id]
        const isFound = currentList.some(i => i.id === internship.id)

        let updatedList = currentList
          .filter(i => i.state !== InternshipStates.REFUSED)
          .map(i => i.id === internship.id ? internship : i)

        if (!isFound && !(startDate > period.endDate || endDate < period.startDate)) {
          updatedList = updatedList.concat(internship)
        }

        newInternshipsMap[internship.sector.id] = updatedList
        newSelectedInternships = newSelectedInternships.map(i => i.id === internship.id ? new Internship(internship) : i)
      } else if (action === Actions.DELETE) {
        newInternshipsMap[internship.sector.id] = newInternshipsMap[internship.sector.id].filter(i => i.id !== internship.id)
        newSelectedInternships = newSelectedInternships.filter(i => i.id !== internship.id)
      }
    }

    setInternshipsMap(newInternshipsMap)
    setSelectedInternships({ ...selectedInternships, values: newSelectedInternships })
  }, [internshipsMap, selectedInternships])

  const seed = useCallback(sector => ({
    institution: getEntityOrNull(user.institutions[0]),
    sector: getEntityOrNull(sector)
  }), [user.institutions[0]])

  return (
    <div className='places-occupancy-view-container flex-row h-100 w-100'>
      <InternshipFormModal keysToValidate={KEYS_TO_VALIDATE} sectors={sectorsOrderByName}>
        <div className='settings-container'>
          <Settings schools={schools} onSelectSectors={handleSectorsChange} onSelectSchools={setSchools} />
        </div>
        <div className='places-occupancy-tables-container flex-column h-100'>
          <div className='places-occupancy-tables-header h-5 w-100 pd-btm-8'>
            <PlacesOccupancyHeader
              addDisabled={isEmptyArray(activeSectors)}
              period={period}
              isYearly={isYearly}
              overlaySettings={overlaySettings}
              school={schools.length === 1 ? firstOrDefault(schools) : null}
              onDateChange={handleDateChange}
              onPeriodChange={handlePeriodChange}
              onInternshipChange={handleInternshipsChange}
              onIsYearlyChange={setIsYearly}
              onOverlaySettingsSelection={handleSelectSetting}
            />
          </div>
          <div className='places-occupancy-tables-content h-95 w-100 scroll-y' onScroll={handleScroll}>
            <HighlightedColumnsProvider>
              {activeSectors.map(s => (
                <div key={s.id} className='flex-column w-100 pd-btm-12'>
                  <h3 style={{ margin: '0' }}>{s.name}</h3>
                  <PlacesOccupancyTable
                    config={config}
                    period={period}
                    school={schools.length === 1 ? firstOrDefault(schools) : null}
                    holidayPeriods={holidays.data}
                    internships={internshipsMap[s.id] ?? []}
                    seed={seed(s)}
                    selectedInternships={selectedInternships}
                    quotasMap={quotasMap[s.id] ?? []}
                    sectionGroups={sectionGroupsMap[s.id] ?? []}
                    studentPathways={studentPathways[s.id] ?? []}
                    onChange={handleInternshipsChange}
                    onSelect={setSelectedInternships}
                  />
                </div>
              ))}
            </HighlightedColumnsProvider>
          </div>
          {isNonEmptyArray(selectedInternships.values) && (
            <InternshipToolBar
              date={selectedInternships.startDate}
              internships={selectedInternships.values}
              onChange={handleInternshipsChange}
              onUnselect={() => setSelectedInternships(DEFAULT_SELECTED_VALUES)}
            />
          )}
        </div>
      </InternshipFormModal>
    </div>
  )
}

export default connect(mapStateToProps)(PlacesOccupancyView)
